import gql from "graphql-tag";
import { useEffect, useRef } from 'react';
import { CarouselPagination, Container, InducteeCard, RevealWrapper, SectionHeader } from "@components";
import { CarouselInducteesFragment } from "@graphqlTypes";
import { FeaturedInducteesFragment } from "@/fragments/FeaturedInductees";
import { Splide, SplideSlide, SplideTrack } from "react-splide-ts";
import { ensurePostsAreInductees } from "@/core/utils";

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from "./CarouselInductees.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

export default function CarouselInductees(props: CarouselInducteesFragment) {
  const { headline, subhead, inductees, cta } = props;
  const showHeader = Boolean(headline || subhead);
  const filteredInductees = ensurePostsAreInductees(inductees);

  // -------------------- Splide

  const splideOptions = {
    type: "slide",
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,
    perPage: 6,
    perMove: 6,
    gap: "3rem",
    padding: {
      left: "5rem",
      right: "5rem",
    },
    breakpoints: {
      // Below 1025px
      1025: {
        perPage: 4,
        perMove: 4,
        gap: "3rem",
        padding: {
          left: "4rem",
          right: "4rem",
        },
        arrows: false,
      },
      // Below 769px
      769: {
        perPage: 2,
        perMove: 2,
        gap: "2rem",
        padding: {
          left: "2rem",
          right: "2rem",
        },
      },
    },
  };

  // Override accessibility attributes
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["carousel-inductees"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
      {showHeader && (
        <Container>
          <SectionHeader
            headline={headline}
            subhead={subhead}
            isRevealEnabled={isRevealEnabled}
            isInView={isInView}
          />
        </Container>
      )}
      <Splide hasTrack={false} options={splideOptions}>
        <SplideTrack className={cx("carousel-inductees__track")}>
          {filteredInductees.map((inductee) => (
            <SplideSlide key={inductee.title} aria-label={inductee.title}>
              <InducteeCard
                className={cx(["splide__slide", "carousel-inductees__inductee"])}
                inductee={inductee}
                showCategory={true}
              />
            </SplideSlide>
          ))}
        </SplideTrack>
        <Container>
          <CarouselPagination cta={cta} className={cx("carousel-inductees__pagination")} />
        </Container>
      </Splide>
      </RevealWrapper>
    </section>
  );
}

CarouselInductees.fragments = {
  entry: gql`
    ${FeaturedInducteesFragment}
    fragment CarouselInductees on FlexiblePageContentContentCarouselInducteesLayout {
      headline
      subhead
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
      inductees {
        ...FeaturedInductees
      }
    }
  `,
};
